/* eslint max-statements: ["error", {max: 23}] -- complex logic */
/* eslint complexity: ["error", {max: 10}] -- complex logic */
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import uuid from "react-native-uuid";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { ErrorSource } from "@/crossPlatform/Datadog";
import { captureException, captureMessage } from "@/services/AppMonitor";
import type { AuthSliceState } from "@/store/reducers/auth";
import { getAuthContext } from "@/utils/apiAuth.util";
import {
  handleUnrecoverableCredentialsApiCallErrorNative,
  handleUnrecoverableCredentialsApiCallErrorWeb,
  isUnrecoverableCredentialsErrorOnApiCall,
} from "@/utils/auth.util";
import { isWeb } from "@/utils/platform.util";

const ApiCallsSuppressedError = {
  error: {
    error: "Auth0 session expired; API calls suppressed",
    status: "FETCH_ERROR" as const,
  },
};

const baseQuery = fetchBaseQuery({
  baseUrl: ProcessEnvironment.ApiGateway,
  prepareHeaders: async (headers) => {
    const { token } = await getAuthContext();
    try {
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
        headers.set("uniqueRequestId", `MyEquip:${uuid.v1()}`);
      }
      return headers;
    } catch {
      return headers;
    }
  },
  timeout: Number(ProcessEnvironment.RequestTimeoutMs),
});

/* eslint-disable max-params -- disable block because can't do eslint-disable-next-line in the middle of this type */
const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

const baseQueryWithErrorLogging: BaseQueryFn<
  FetchArgs | string,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  /* eslint-enable max-params */
  const { endpoint } = api;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const state = api.getState() as { auth: AuthSliceState };
  const { shouldPreventApiCallsDueToExpiredToken: isExpiredAuth } = state.auth;

  // if a user's auth0 session is expired, do not attempt further API calls,
  // as they would fail/retry on 4xx access errors
  if (isExpiredAuth) {
    return ApiCallsSuppressedError;
  }

  try {
    const result = await baseQueryWithRetry(args, api, extraOptions);

    if (result.error) {
      const { error } = result;
      const { data: errorData, status } = error;

      // mobile may also benefit from this extra error-checking clause,
      // however limited to strictly web so that this web-only change does not impact mobile
      // eslint-disable-next-line max-depth
      if (isWeb() && isUnrecoverableCredentialsErrorOnApiCall(error)) {
        handleUnrecoverableCredentialsApiCallErrorWeb({ api, error });
      }

      const message = `${endpoint} endpoint returned error of type ${status}`;

      const data = errorData
        ? JSON.stringify(errorData)
        : JSON.stringify(error);

      captureMessage(message, {
        context: {
          data,
          endpoint,
          status,
        },
        errorSource: ErrorSource.NETWORK,
      });
    }

    return result;
  } catch (error) {
    captureException(error, {
      context: {
        endpoint,
      },
    });

    if (!isWeb()) {
      // handleNative internally exits early if error is actually recoverable
      handleUnrecoverableCredentialsApiCallErrorNative({ api, error });
    } else if (isUnrecoverableCredentialsErrorOnApiCall(error)) {
      handleUnrecoverableCredentialsApiCallErrorWeb({ api, error });
      return ApiCallsSuppressedError;
    }

    throw error;
  }
};

const baseQueryWithOptions = baseQueryWithErrorLogging;
export default baseQueryWithOptions;
